import { 
  Box, 
  Typography, 
  TextField, 
  Button, 
  IconButton, 
  Container, 
  Card, 
  CardContent, 
  Avatar, 
  Stack,
  Chip,
  CircularProgress 
} from "@mui/material";
import EmailIcon from "@mui/icons-material/Email";
import PhoneIcon from "@mui/icons-material/Phone";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import InstagramIcon from "@mui/icons-material/Instagram";
import { WhatsApp } from "@mui/icons-material";
import LinkedInIcon from "@mui/icons-material/LinkedIn";
import LanguageIcon from "@mui/icons-material/Language";
import SendIcon from "@mui/icons-material/Send";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import MaskedInput from "../../components/MaskedInput";
import { useEffect, useState, useRef } from "react";
import { useCreateLead } from "../../../client/hooks/users/user.queries";
import CustomSnackbar from "../../components/Message.Component";

export default function ContactPage({ theme }) {
  const [alert, setAlert] = useState(false);
  const [successMsg, setSuccessMsg] = useState("");
  const [errorMsg, setErrorMsg] = useState("");
  const [performSendLead, setPerformSendLead] = useState(false);
  const [lead, setLead] = useState({});
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef(null);
  
  const { data, isError, isLoading, refetch } = useCreateLead(
    lead,
    performSendLead
  );
  
  const [phone, setPhone] = useState("");
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    message: "",
  });
  const [errors, setErrors] = useState({
    name: false,
    email: false,
  });

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });

    if (errors[name]) {
      setErrors({ ...errors, [name]: false });
    }
  };

  const handleSubmit = () => {
    const newErrors = {
      name: !formData.name.trim(),
      email: !formData.email.trim(),
    };

    setErrors(newErrors);

    if (!newErrors.name && !newErrors.email) {
      let led = { ...formData, phone };
      setLead(led);
      setTimeout(() => {
        if (!isError) setPerformSendLead(true);
        if (isError) refetch();
      }, 200);
      setFormData({
        name: "",
        email: "",
        message: "",
      });
      setPhone("");
    }
  };

  useEffect(() => {
    if (data && !isError) {
      setAlert(true);
      setSuccessMsg(
        "Obrigado pelo interesse! Em breve entraremos em contato com você."
      );
    }

    if (isError) {
      setAlert(true);
      setErrorMsg("Ocorreu um erro inesperado.");
    }
  }, [isLoading, isError, data]);

  const contactInfo = [
    {
      icon: <EmailIcon sx={{ fontSize: 28 }} />,
      title: "Email",
      value: "jonas@walaplace.com.br",
      subtitle: "Resposta em até 2 horas",
      color: "#1A73E8",
    },
    {
      icon: <PhoneIcon sx={{ fontSize: 28 }} />,
      title: "Telefone",
      value: "(51) 9 9797-1635",
      subtitle: "Seg à Sex, 8h às 18h",
      color: "#38A169",
    },
    {
      icon: <LocationOnIcon sx={{ fontSize: 28 }} />,
      title: "Endereço",
      value: "Santa Cruz do Sul - RS",
      subtitle: "Rua Carlos Maurício Werlang 288",
      color: "#FF7323",
    },
  ];

  const socialLinks = [
    {
      icon: <WhatsApp sx={{ fontSize: 24 }} />,
      name: "WhatsApp",
      url: "https://wa.me/5551997971635?text=Quero%20saber%20sobre%20o%20wala%20smart%20work",
      color: "#25D366",
    },
    {
      icon: <InstagramIcon sx={{ fontSize: 24 }} />,
      name: "Instagram",
      url: "https://www.instagram.com/wala_smartwork/",
      color: "#E4405F",
    },
    {
      icon: <LinkedInIcon sx={{ fontSize: 24 }} />,
      name: "LinkedIn",
      url: "https://www.linkedin.com/company/wala-smart-work/about/",
      color: "#0077B5",
    },
    {
      icon: <LanguageIcon sx={{ fontSize: 24 }} />,
      name: "Website",
      url: "https://www.linkedin.com/company/wala-smart-work/about/",
      color: "#6B46C1",
    },
  ];

  return (
    <>
      <CustomSnackbar
        open={alert}
        onClose={() => setAlert(false)}
        severity={isError ? "error" : "success"}
        message={isError ? errorMsg : successMsg}
      />
      
      <Box
        ref={sectionRef}
        sx={{
          py: { xs: 8, md: 12 },
          background: `
            linear-gradient(135deg, 
              rgba(255, 115, 35, 0.03) 0%, 
              rgba(26, 115, 232, 0.02) 50%, 
              rgba(255, 255, 255, 1) 100%
            )
          `,
          position: "relative",
          overflow: "hidden",
        }}
      >
        {/* Background Elements */}
        <Box
          sx={{
            position: "absolute",
            bottom: -200,
            right: -200,
            width: 500,
            height: 500,
            background: "radial-gradient(circle, rgba(255, 115, 35, 0.05) 0%, transparent 70%)",
            borderRadius: "50%",
            zIndex: 0,
          }}
        />

        <Container maxWidth="xl" sx={{ position: "relative", zIndex: 1 }}>
          {/* Header */}
          <Box
            sx={{
              textAlign: "center",
              mb: 8,
              transform: isVisible ? "translateY(0)" : "translateY(50px)",
              opacity: isVisible ? 1 : 0,
              transition: "all 0.8s cubic-bezier(0.4, 0, 0.2, 1)",
            }}
          >
            <Chip
              label="📞 Vamos Conversar"
              sx={{
                mb: 3,
                backgroundColor: "rgba(255, 115, 35, 0.1)",
                color: theme.palette.primary.main,
                fontWeight: 600,
                fontSize: "1rem",
                padding: "12px 20px",
                borderRadius: "50px",
                border: "1px solid rgba(255, 115, 35, 0.2)",
              }}
            />

            <Typography
              variant="h2"
              sx={{
                fontSize: { xs: "2.5rem", md: "3.5rem" },
                fontWeight: 800,
                mb: 3,
                background: "linear-gradient(135deg, #FF7323 0%, #D85A00 50%, #1A73E8 100%)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                backgroundClip: "text",
              }}
            >
              Entre em Contato
            </Typography>

            <Typography
              variant="h5"
              sx={{
                color: theme.palette.text.secondary,
                fontSize: { xs: "1.2rem", md: "1.4rem" },
                maxWidth: "700px",
                mx: "auto",
                lineHeight: 1.6,
              }}
            >
              Vamos juntos <strong>automatizar seu negócio</strong>! Preencha o formulário 
              ou entre em contato diretamente conosco.
            </Typography>
          </Box>

          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', lg: 'row' },
              alignItems: 'stretch',
              gap: { xs: 4, lg: 6 }
            }}
          >
            {/* Contact Form */}
            <Box sx={{ flex: 1, maxWidth: { lg: '58%' } }}>
              <Card
                sx={{
                  borderRadius: "24px",
                  border: "1px solid rgba(255, 115, 35, 0.1)",
                  boxShadow: "0px 8px 32px rgba(0, 0, 0, 0.08)",
                  background: "rgba(255, 255, 255, 0.9)",
                  backdropFilter: "blur(20px)",
                  height: "100%",
                  transform: isVisible ? "translateX(0)" : "translateX(-50px)",
                  opacity: isVisible ? 1 : 0,
                  transition: "all 0.8s cubic-bezier(0.4, 0, 0.2, 1) 0.2s",
                }}
              >
                <CardContent sx={{ p: { xs: 4, md: 6 } }}>
                  <Typography
                    variant="h4"
                    sx={{
                      fontWeight: 700,
                      mb: 2,
                      color: theme.palette.text.primary,
                      fontSize: { xs: "1.8rem", md: "2.2rem" },
                    }}
                  >
                    Solicite uma Demonstração
                  </Typography>
                  
                  <Typography
                    variant="body1"
                    sx={{
                      color: theme.palette.text.secondary,
                      mb: 4,
                      fontSize: "1.1rem",
                      lineHeight: 1.6,
                    }}
                  >
                    Preencha os dados abaixo e nossa equipe entrará em contato 
                    para apresentar como podemos transformar seu e-commerce.
                  </Typography>

                  <Stack spacing={3}>
                    <TextField
                      label="Nome Completo"
                      fullWidth
                      variant="outlined"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      error={errors.name}
                      helperText={errors.name && "O nome é obrigatório"}
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          borderRadius: "12px",
                          backgroundColor: "rgba(255, 255, 255, 0.8)",
                        },
                      }}
                    />
                    
                    <TextField
                      label="Email Corporativo"
                      fullWidth
                      variant="outlined"
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      error={errors.email}
                      helperText={errors.email && "O e-mail é obrigatório"}
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          borderRadius: "12px",
                          backgroundColor: "rgba(255, 255, 255, 0.8)",
                        },
                      }}
                    />
                    
                    <MaskedInput
                      mask="(99) 99999-9999"
                      label="Telefone/WhatsApp"
                      value={phone}
                      onChange={(e) => setPhone(e.target.value)}
                      fullWidth
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          borderRadius: "12px",
                          backgroundColor: "rgba(255, 255, 255, 0.8)",
                        },
                      }}
                    />
                    
                    <TextField
                      label="Conte-nos sobre seu negócio"
                      fullWidth
                      variant="outlined"
                      multiline
                      rows={4}
                      name="message"
                      value={formData.message}
                      onChange={handleInputChange}
                      placeholder="Qual o seu marketplace principal? Quantos pedidos por dia? Quais os principais desafios?"
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          borderRadius: "12px",
                          backgroundColor: "rgba(255, 255, 255, 0.8)",
                        },
                      }}
                    />
                    
                    <Button
                      onClick={handleSubmit}
                      disabled={isLoading}
                      variant="contained"
                      size="large"
                      startIcon={isLoading ? <CircularProgress size={20} color="inherit" /> : <SendIcon />}
                      sx={{
                        background: "linear-gradient(135deg, #FF7323 0%, #D85A00 100%)",
                        color: "white",
                        fontWeight: 600,
                        fontSize: "1.1rem",
                        padding: "16px 32px",
                        borderRadius: "12px",
                        mt: 2,
                        "&:hover": {
                          background: "linear-gradient(135deg, #D85A00 0%, #B84700 100%)",
                          transform: "translateY(-2px)",
                          boxShadow: "0px 8px 25px rgba(255, 115, 35, 0.4)",
                        },
                      }}
                    >
                      {isLoading ? "Enviando..." : "Solicitar Demonstração"}
                    </Button>
                  </Stack>

                  {/* Trust Indicators */}
                  <Stack direction="row" spacing={2} sx={{ mt: 4, flexWrap: "wrap", gap: 1 }}>
                    <Chip
                      icon={<CheckCircleIcon sx={{ fontSize: 16 }} />}
                      label="Resposta em 2 horas"
                      size="small"
                      sx={{
                        backgroundColor: "rgba(56, 161, 105, 0.1)",
                        color: "#38A169",
                        fontWeight: 500,
                      }}
                    />
                    <Chip
                      icon={<CheckCircleIcon sx={{ fontSize: 16 }} />}
                      label="Demonstração gratuita"
                      size="small"
                      sx={{
                        backgroundColor: "rgba(26, 115, 232, 0.1)",
                        color: "#1A73E8",
                        fontWeight: 500,
                      }}
                    />
                    <Chip
                      icon={<CheckCircleIcon sx={{ fontSize: 16 }} />}
                      label="Sem compromisso"
                      size="small"
                      sx={{
                        backgroundColor: "rgba(255, 115, 35, 0.1)",
                        color: "#FF7323",
                        fontWeight: 500,
                      }}
                    />
                  </Stack>
                </CardContent>
              </Card>
            </Box>

            {/* Contact Information */}
            <Box sx={{ flex: 1, maxWidth: { lg: '42%' } }}>
              <Box
                sx={{
                  transform: isVisible ? "translateX(0)" : "translateX(50px)",
                  opacity: isVisible ? 1 : 0,
                  transition: "all 0.8s cubic-bezier(0.4, 0, 0.2, 1) 0.4s",
                }}
              >
                {/* Contact Cards */}
                <Stack spacing={3} sx={{ mb: 4 }}>
                  {contactInfo.map((info, index) => (
                    <Card
                      key={index}
                      sx={{
                        borderRadius: "16px",
                        border: "1px solid rgba(255, 115, 35, 0.1)",
                        background: "rgba(255, 255, 255, 0.8)",
                        backdropFilter: "blur(10px)",
                        transition: "all 0.3s ease",
                        "&:hover": {
                          transform: "translateY(-3px)",
                          boxShadow: "0px 12px 30px rgba(0, 0, 0, 0.1)",
                        },
                      }}
                    >
                      <CardContent sx={{ p: 3 }}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                          <Avatar
                            sx={{
                              backgroundColor: `${info.color}15`,
                              color: info.color,
                              width: 56,
                              height: 56,
                            }}
                          >
                            {info.icon}
                          </Avatar>
                          <Box>
                            <Typography
                              variant="h6"
                              sx={{
                                fontWeight: 600,
                                color: theme.palette.text.primary,
                                mb: 0.5,
                              }}
                            >
                              {info.title}
                            </Typography>
                            <Typography
                              variant="body1"
                              sx={{
                                color: theme.palette.text.primary,
                                fontWeight: 500,
                                mb: 0.5,
                              }}
                            >
                              {info.value}
                            </Typography>
                            <Typography
                              variant="body2"
                              sx={{ color: theme.palette.text.secondary }}
                            >
                              {info.subtitle}
                            </Typography>
                          </Box>
                        </Box>
                      </CardContent>
                    </Card>
                  ))}
                </Stack>

                {/* Social Links */}
                <Card
                  sx={{
                    borderRadius: "16px",
                    border: "1px solid rgba(255, 115, 35, 0.1)",
                    background: "rgba(255, 255, 255, 0.8)",
                    backdropFilter: "blur(10px)",
                  }}
                >
                  <CardContent sx={{ p: 3 }}>
                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: 600,
                        color: theme.palette.text.primary,
                        mb: 3,
                        textAlign: "center",
                      }}
                    >
                      Siga-nos nas Redes Sociais
                    </Typography>
                    
                    <Stack direction="row" justifyContent="center" spacing={2}>
                      {socialLinks.map((social, index) => (
                        <IconButton
                          key={index}
                          component="a"
                          href={social.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          sx={{
                            backgroundColor: `${social.color}15`,
                            color: social.color,
                            width: 48,
                            height: 48,
                            transition: "all 0.3s ease",
                            "&:hover": {
                              backgroundColor: social.color,
                              color: "white",
                              transform: "translateY(-3px)",
                              boxShadow: `0px 8px 20px ${social.color}40`,
                            },
                          }}
                        >
                          {social.icon}
                        </IconButton>
                      ))}
                    </Stack>
                  </CardContent>
                </Card>
              </Box>
            </Box>
          </Box>
        </Container>
      </Box>
    </>
  );
}
